import NoiseFetchLayer from './NoiseFetchLayer';

function escapeHtml(value) {
    return String(value == null ? '' : value)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#039;');
}

function safeUrl(value) {
    try {
        const url = new URL(String(value || ''));
        return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : '';
    } catch (_error) {
        return '';
    }
}

export function noiseTypeLabel(value) {
    return {
        road: 'Hałas drogowy',
        rail: 'Hałas kolejowy',
        air: 'Hałas lotniczy',
        agglomeration: 'Hałas na obszarze miejskim'
    }[value] || 'Strategiczna mapa hałasu';
}

function sourceNameLabel(value) {
    const name = String(value || '');
    return name.toLowerCase() === 'european environment agency'
        ? 'Europejska Agencja Środowiska'
        : (name || 'Europejska Agencja Środowiska');
}

export function indicatorLabel(value) {
    return value === 'LN'
        ? 'Długookresowy poziom hałasu w nocy (LN)'
        : 'Długookresowy poziom hałasu dla całej doby (LDWN)';
}

/** The time of day of a strategic noise map in a chip, with its symbol in brackets. */
export function indicatorShortLabel(value) {
    return String(value || '').toUpperCase() === 'LN' ? 'Noc (LN)' : 'Cała doba (LDWN)';
}

export function contourGeometryLevel(zoom) {
    const value = Number(zoom);
    if (Number.isFinite(value) && value >= 14) return 14;
    if (Number.isFinite(value) && value >= 12) return 12;
    if (Number.isFinite(value) && value >= 10) return 10;
    if (Number.isFinite(value) && value >= 8) return 8;
    return 6;
}

export function bandDescription(minimum) {
    const value = Number(minimum);
    if (!Number.isFinite(value) || value < 55) return { key: 'low', label: 'Niższy poziom na tej mapie' };
    if (value < 60) return { key: 'moderate', label: 'Umiarkowanie głośno' };
    if (value < 65) return { key: 'high', label: 'Głośno' };
    if (value < 70) return { key: 'very_high', label: 'Bardzo głośno' };
    return { key: 'very_high', label: 'Wyjątkowo głośno' };
}

export function bandLabel(properties) {
    const minimum = properties.dbMin == null ? NaN : Number(properties.dbMin);
    const maximum = properties.dbMax == null ? NaN : Number(properties.dbMax);
    if (Number.isFinite(minimum) && Number.isFinite(maximum)) return minimum + '–' + maximum + ' decybeli';
    if (Number.isFinite(minimum)) return 'co najmniej ' + minimum + ' decybeli';
    return 'Brak podanego przedziału';
}

export function contourHtml(properties, metadata) {
    const p = properties || {};
    const provenance = (metadata && metadata.provenance) || {};
    const assessment = bandDescription(p.dbMin);
    const source = sourceNameLabel(provenance.sourceName || provenance.provider);
    const url = safeUrl(provenance.sourceUrl);
    const approximation = (metadata && metadata.approximationMessage)
        || 'Granice na mapie są uproszczone. Przy granicach wynik może różnić się od danych źródłowych, a małe lub wąskie obszary mogą być pominięte. Brak wyniku nie oznacza braku hałasu.';
    const row = (label, value) => '<div class="nm-layer-info-row"><span>' + escapeHtml(label)
        + '</span><span>' + escapeHtml(value) + '</span></div>';
    return '<div class="nm-noise-popup"><strong>Strategiczna mapa hałasu</strong>'
        + '<div class="nm-layer-info-copy">' + escapeHtml(noiseTypeLabel(p.noiseType)) + '</div>'
        + '<div class="nm-noise-reading nm-noise-reading--' + assessment.key + '"><b>'
        + escapeHtml(assessment.label) + '</b><span>' + escapeHtml(bandLabel(p)) + '</span></div>'
        + row('Pora', indicatorLabel(p.indicator))
        + (p.referenceYear ? row('Rok danych', p.referenceYear) : '')
        + '<div class="nm-layer-info-copy">Kolor pokazuje modelowany przedział hałasu dla tego obszaru. To nie jest pomiar na działce ani ocena, czy zostały przekroczone normy.</div>'
        + '<div class="nm-layer-info-copy">' + escapeHtml(approximation) + '</div>'
        + '<div class="nm-layer-info-copy">Źródło: ' + escapeHtml(source) + '</div>'
        + (url ? '<div class="nm-external-links"><a class="ms-link nm-ext-link" href="' + escapeHtml(url)
            + '" target="_blank" rel="noopener noreferrer">Zobacz dane źródłowe ↗</a></div>' : '')
        + '</div>';
}

export default class NoiseContourLayer extends NoiseFetchLayer {
    static MIN_ZOOM = 6;

    constructor(config = {}) {
        super(config);
        this.indicator = String(config.indicator || 'LDWN').toUpperCase() === 'LN' ? 'LN' : 'LDWN';
        this._boundMap = null;
        this._renderedGeometryLevel = null;
        this._requestedGeometryLevel = null;
    }

    getSourceId() {
        return 'src-' + this.id;
    }

    getLayerIds() {
        return ['lyr-' + this.id + '-fill', 'lyr-' + this.id + '-line'];
    }

    shouldUpdateSourceInPlace() {
        return true;
    }

    buildUrl() {
        const bbox = this.config.getCurrentBbox ? this.config.getCurrentBbox(0.12) : '';
        const zoom = this.config.getCurrentZoom ? this.config.getCurrentZoom() : 12;
        let url = this.config.baseUrl + 'rcn/noise-api/contours?bbox=' + encodeURIComponent(bbox)
            + '&zoom=' + encodeURIComponent(zoom)
            + '&indicator=' + encodeURIComponent(this.indicator)
            + '&limit=1000';
        if (this.config.noiseType) {
            url += '&noise_type=' + encodeURIComponent(this.config.noiseType);
        }
        return url;
    }

    beforeRequest() {
        const map = this.getMap();
        const zoom = map && map.getZoom ? map.getZoom() : NoiseContourLayer.MIN_ZOOM;
        this._requestedGeometryLevel = contourGeometryLevel(zoom);
    }

    handleError(error) {
        if (this._renderedGeometryLevel !== null
            && this._requestedGeometryLevel !== null
            && this._renderedGeometryLevel !== this._requestedGeometryLevel) {
            this._unbindInteractions();
            this.removeLayerFromMap();
            this._renderedGeometryLevel = null;
        }
        super.handleError(error);
    }

    bandColorExpression(alpha) {
        return [
            'step',
            ['coalesce', ['get', 'dbMin'], 0],
            'rgba(34, 197, 94, ' + alpha + ')',
            55, 'rgba(234, 179, 8, ' + alpha + ')',
            60, 'rgba(249, 115, 22, ' + alpha + ')',
            65, 'rgba(239, 68, 68, ' + alpha + ')',
            70, 'rgba(168, 85, 247, ' + alpha + ')',
            75, 'rgba(88, 28, 135, ' + alpha + ')'
        ];
    }

    _unbindInteractions() {
        if (!this._boundMap) return;
        this.getLayerIds().forEach((layerId) => {
            this._boundMap.off('mouseenter', layerId, this._onEnter);
            this._boundMap.off('mouseleave', layerId, this._onLeave);
        });
        this._boundMap = null;
    }

    _bindInteractions(map) {
        if (this._boundMap === map) return;
        this._unbindInteractions();
        this._onEnter = () => { map.getCanvas().style.cursor = 'pointer'; };
        this._onLeave = () => { map.getCanvas().style.cursor = ''; };
        this.getLayerIds().forEach((layerId) => {
            map.on('mouseenter', layerId, this._onEnter);
            map.on('mouseleave', layerId, this._onLeave);
        });
        this._boundMap = map;
    }

    _addLayers(map, sourceId) {
        const layerIds = this.getLayerIds();
        map.addLayer({
            id: layerIds[0],
            type: 'fill',
            source: sourceId,
            layout: {
                'fill-sort-key': ['coalesce', ['get', 'dbMin'], 0]
            },
            paint: {
                'fill-color': this.bandColorExpression(0.34),
                'fill-opacity': 1
            }
        });
        map.addLayer({
            id: layerIds[1],
            type: 'line',
            source: sourceId,
            paint: {
                'line-color': this.bandColorExpression(0.9),
                'line-width': [
                    'interpolate',
                    ['linear'],
                    ['zoom'],
                    9, 0.7,
                    15, 1.7
                ]
            }
        });
        this._bindInteractions(map);
    }

    renderData(data) {
        this._metadata = (data && data.metadata) || {};
        this._renderedGeometryLevel = Number(this._metadata.geometryLevel) || this._requestedGeometryLevel;
        if (this.config.onMetadata) {
            this.config.onMetadata(this._metadata);
        }
        return this.renderSourceData(data, this._addLayers.bind(this));
    }

    clear() {
        this._unbindInteractions();
        this._renderedGeometryLevel = null;
        this._requestedGeometryLevel = null;
        super.clear();
    }
}
